import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  Briefcase,
  Download,
  FileSpreadsheet,
  Archive,
  FolderOpen,
  Layers,
  Pencil,
  Plus,
  RotateCcw,
  Search,
  Trash2,
  Upload,
  Users,
  Wallet,
  X,
  ShieldCheck,
} from "lucide-react";
import StatusBadge from "../../components/dashboard/StatusBadge";
import StatCard from "../../components/dashboard/StatCard";
import StatCardGrid from "../../components/dashboard/StatCardGrid";
import ListPagination from "../../components/dashboard/ListPagination";
import TableActionsMenu from "../../components/dashboard/TableActionsMenu";
import { tableActionClass } from "../../components/dashboard/tableCellContent";
import Button from "../../components/ui/Button";
import { clearPortfolioUploadsCache } from "../../services/portfolioUploadsApi";
import { usePortfolios } from "../../context/PortfolioContext";
import {
  enrichPortfolioGroup,
  fetchTrashedPortfolioGroups,
  permanentlyDeletePortfolioGroup,
  restorePortfolioGroup,
} from "../../services/portfolioGroupsApi";
import usePagination from "../../hooks/usePagination";
import {
  formatCurrency,
  formatDate,
  formatPercent,
} from "../../data/mock/dashboardData";
import { showError, showSuccess } from "../../utils/toast";
import { API_BASE_URL } from "../../config/api";

const GROUPS_PAGE_SIZE = 8;
const UPLOADS_PAGE_SIZE = 5;

const STAGE_LABELS = {
  processing: "Processing",
  cases_created: "Cases created",
  validated: "Validated",
};

function GroupFormPanel({ open, onClose, onSubmit, group = null, saving = false }) {
  const isEdit = Boolean(group);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  useEffect(() => {
    if (!open) return;
    setName(group?.name ?? "");
    setDescription(group?.description ?? "");
  }, [open, group]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || saving) return;
    await onSubmit({ name: name.trim(), description: description.trim() });
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:p-6">
      <button
        type="button"
        className="absolute inset-0 bg-primary-dark/55 backdrop-blur-sm"
        aria-label="Close"
        onClick={() => !saving && onClose()}
      />
      <motion.form
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        onSubmit={handleSubmit}
        className="relative w-full max-w-lg rounded-t-2xl bg-white p-6 shadow-2xl sm:rounded-2xl"
      >
        <div className="mb-5 flex items-start justify-between">
          <div>
            <h2 className="text-lg font-bold text-primary">
              {isEdit ? "Edit group name" : "New portfolio group"}
            </h2>
            <p className="mt-1 text-sm text-primary/45">
              {isEdit
                ? "Update the group name or description."
                : "Create a group, then upload one Excel file under it."}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="rounded-lg p-2 text-primary/40 hover:bg-primary/[0.04]"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="mb-1.5 block text-xs font-semibold text-primary/50">Group name</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Telecel Debt Portfolio"
              className="w-full rounded-xl border border-primary/10 px-4 py-3 text-sm focus:border-accent/50 focus:outline-none focus:ring-2 focus:ring-accent/20"
              autoFocus
            />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-semibold text-primary/50">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              placeholder="What accounts belong in this group?"
              className="w-full resize-none rounded-xl border border-primary/10 px-4 py-3 text-sm focus:border-accent/50 focus:outline-none focus:ring-2 focus:ring-accent/20"
            />
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <Button type="button" variant="ghost" size="sm" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" size="sm" disabled={!name.trim()} loading={saving}>
            {isEdit ? "Save changes" : "Create group"}
          </Button>
        </div>
      </motion.form>
    </div>
  );
}

function DeleteGroupModal({ group, saving, onClose, onConfirm }) {
  useEffect(() => {
    const onKey = (event) => {
      if (event.key === "Escape" && !saving) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, saving]);

  if (!group) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-primary/40 p-4 backdrop-blur-sm sm:items-center"
      onClick={() => !saving && onClose()}
    >
      <div
        role="dialog"
        aria-modal="true"
        className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-primary/[0.06] px-5 py-4">
          <h2 className="text-lg font-bold text-primary">Move group to deleted</h2>
          <p className="mt-1 text-sm text-primary/55">
            Move <span className="font-semibold text-primary">{group.name}</span> to Deleted
            groups? This soft-deletes the group, and only the cases and portfolio records attached
            to it, when none of those cases have been worked on. A case that is not attached to
            this group’s portfolio records blocks the delete. Customers who still have a case in
            another group stay. You can restore the group afterwards, or delete it permanently
            from Deleted groups.
          </p>
        </div>
        <div className="flex justify-end gap-2 px-5 py-4">
          <Button variant="ghost" size="sm" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            size="sm"
            className="bg-red-600 text-white shadow-md shadow-red-600/20 hover:bg-red-700"
            loading={saving}
            onClick={onConfirm}
          >
            Move to deleted
          </Button>
        </div>
      </div>
    </div>
  );
}

async function downloadPortfolioFile(token, upload) {
  const uploadId = upload.numericId ?? upload.id;
  const response = await fetch(
    `${API_BASE_URL}/api/provs/portfolios/file-uploads/${encodeURIComponent(uploadId)}/download`,
    {
      headers: {
        Accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    }
  );
  if (!response.ok) {
    throw new Error("Could not export this portfolio file.");
  }
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = upload.fileName || "portfolio.xlsx";
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function UploadsDrawer({ group, open, onClose, canUpload }) {
  const token = useSelector((state) => state.auth.token);
  const [exportingId, setExportingId] = useState(null);
  const uploads = group?.uploads ?? [];
  const {
    page,
    totalPages,
    paginatedItems,
    goToPage,
    totalItems,
    startIndex,
    endIndex,
    resetPage,
  } = usePagination(uploads, UPLOADS_PAGE_SIZE);

  useEffect(() => {
    if (open) resetPage();
  }, [group?.id, open]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open || !group) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <button type="button" className="absolute inset-0 bg-primary-dark/45 backdrop-blur-sm" aria-label="Close" onClick={onClose} />
      <motion.aside
        initial={{ x: 40, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        exit={{ x: 40, opacity: 0 }}
        className="relative flex h-full w-full max-w-xl flex-col bg-white shadow-2xl"
      >
        <div className="border-b border-primary/[0.06] px-5 py-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-primary/35">Upload files</p>
              <h2 className="mt-1 text-lg font-bold text-primary">{group.name}</h2>
              <p className="mt-0.5 text-sm text-primary/45">
                {uploads.length} file{uploads.length !== 1 ? "s" : ""} · Created {formatDate(group.createdAt)}
              </p>
            </div>
            <button type="button" onClick={onClose} className="rounded-lg p-2 text-primary/40 hover:bg-primary/[0.04]">
              <X className="h-5 w-5" />
            </button>
          </div>
          {canUpload && !group.hasPortfolioData && uploads.length === 0 ? (
            <Link
              to={`/dashboard/portfolios/${group.id}/upload`}
              className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-collect px-4 py-2.5 text-sm font-bold text-white shadow-md shadow-collect/20"
            >
              <Upload className="h-4 w-4" />
              Upload file
              <ArrowRight className="h-3.5 w-3.5 opacity-70" />
            </Link>
          ) : uploads.length > 0 ? (
            <p className="mt-4 rounded-xl border border-primary/10 bg-primary/[0.03] px-3 py-2.5 text-xs text-primary/55">
              This group already has an upload. Create a new group to upload another file.
            </p>
          ) : null}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {uploads.length === 0 ? (
            <div className="rounded-xl border border-dashed border-primary/12 px-6 py-14 text-center">
              <FileSpreadsheet className="mx-auto h-10 w-10 text-primary/20" />
              {group.numberOfCases > 0 || group.portfoliosCount > 0 ? (
                <>
                  <p className="mt-3 font-semibold text-primary">Portfolio records are already in this group</p>
                  <p className="mt-1 text-sm text-primary/40">
                    {group.numberOfCases.toLocaleString()}{" "}
                    {group.numberOfCases === 1 ? "case" : "cases"} and{" "}
                    {group.portfoliosCount.toLocaleString()}{" "}
                    {group.portfoliosCount === 1 ? "portfolio record" : "portfolio records"} are
                    still here. There is no need to upload the file again.
                  </p>
                  <Link
                    to={`/dashboard/portfolios/${group.id}/cases`}
                    className="mt-4 inline-flex items-center justify-center gap-2 text-sm font-bold text-primary underline"
                  >
                    View group cases
                  </Link>
                </>
              ) : (
                <>
                  <p className="mt-3 font-semibold text-primary">No uploads yet</p>
                  <p className="mt-1 text-sm text-primary/40">Upload an Excel file to import debtor accounts into this group.</p>
                </>
              )}
            </div>
          ) : (
            <ul className="divide-y divide-primary/[0.05]">
              {paginatedItems.map((upload) => (
                <li key={upload.id} className="flex flex-col gap-3 py-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex min-w-0 items-center gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600">
                      <FileSpreadsheet className="h-5 w-5" />
                    </span>
                    <div className="min-w-0">
                      <p className="truncate font-semibold text-primary">{upload.fileName}</p>
                      <p className="mt-0.5 font-mono text-[11px] text-primary/45">
                        Upload ID: {upload.numericId ?? upload.id}
                      </p>
                      <p className="text-xs text-primary/40">
                        {upload.accounts.toLocaleString()} accounts · {formatCurrency(upload.totalOwed)} ·{" "}
                        {formatDate(upload.uploadedAt)}
                      </p>
                      {upload.validationPassRate != null && (
                        <p className="mt-0.5 inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700/80">
                          <ShieldCheck className="h-3 w-3" />
                          {formatPercent(upload.validationPassRate)} validation pass
                          {upload.invalidRows
                            ? ` · ${upload.invalidRows} invalid row${upload.invalidRows === 1 ? "" : "s"}`
                            : ""}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Link
                      to={`/dashboard/portfolios/${group.id}/uploads/${upload.id}`}
                      className={tableActionClass}
                    >
                      <Briefcase className="h-3.5 w-3.5" />
                      View cases
                    </Link>
                    <button
                      type="button"
                      className={tableActionClass}
                      disabled={exportingId === upload.id}
                      onClick={async () => {
                        setExportingId(upload.id);
                        try {
                          await downloadPortfolioFile(token, upload);
                          showSuccess("Portfolio file exported.");
                        } catch (error) {
                          showError(error.message || "Could not export this portfolio file.");
                        } finally {
                          setExportingId(null);
                        }
                      }}
                    >
                      <Download className="h-3.5 w-3.5" />
                      {exportingId === upload.id ? "Exporting…" : "Export file"}
                    </button>
                    <StatusBadge status={upload.status} />
                    {upload.stage && (
                      <span className="rounded-md bg-primary/[0.05] px-2 py-0.5 text-[10px] font-semibold text-primary/60">
                        {STAGE_LABELS[upload.stage] ?? upload.stage}
                      </span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <ListPagination
          page={page}
          totalPages={totalPages}
          totalItems={totalItems}
          startIndex={startIndex}
          endIndex={endIndex}
          goToPage={goToPage}
          pageSize={UPLOADS_PAGE_SIZE}
          alwaysShow
        />
      </motion.aside>
    </div>
  );
}

function PermanentDeleteModal({ group, saving, onClose, onConfirm }) {
  useEffect(() => {
    const onKey = (event) => {
      if (event.key === "Escape" && !saving) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, saving]);

  if (!group) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-primary/40 p-4 backdrop-blur-sm sm:items-center"
      onClick={() => !saving && onClose()}
    >
      <div
        role="dialog"
        aria-modal="true"
        className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-primary/[0.06] px-5 py-4">
          <h2 className="text-lg font-bold text-primary">Delete permanently</h2>
          <p className="mt-1 text-sm text-primary/55">
            Permanently delete{" "}
            <span className="font-semibold text-primary">{group.name}</span> and the{" "}
            {group.numberOfCases.toLocaleString()}{" "}
            {group.numberOfCases === 1 ? "case" : "cases"} and{" "}
            {group.portfoliosCount.toLocaleString()}{" "}
            {group.portfoliosCount === 1 ? "portfolio record" : "portfolio records"} that were
            removed with it? This cannot be restored. Customers who still belong to another group
            stay.
          </p>
        </div>
        <div className="flex justify-end gap-2 px-5 py-4">
          <Button variant="ghost" size="sm" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            size="sm"
            className="bg-red-600 text-white shadow-md shadow-red-600/20 hover:bg-red-700"
            loading={saving}
            onClick={onConfirm}
          >
            Delete permanently
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function PortfoliosPage() {
  const token = useSelector((state) => state.auth.token);
  const { groups, groupsLoading, groupsError, uploadsError, addGroup, updateGroup, removeGroup, refreshGroups } =
    usePortfolios();
  // Single institution login — no provider-side RBAC gates.
  const canUpload = true;
  const canManage = true;

  const [formOpen, setFormOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [savingGroup, setSavingGroup] = useState(false);
  const [deletingGroup, setDeletingGroup] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [trashedGroups, setTrashedGroups] = useState([]);
  const [trashedLoading, setTrashedLoading] = useState(false);
  const [trashedError, setTrashedError] = useState(null);
  const [restoringId, setRestoringId] = useState(null);
  const [purgingGroup, setPurgingGroup] = useState(null);
  const [purging, setPurging] = useState(false);
  const [uploadsGroupId, setUploadsGroupId] = useState(null);
  const [search, setSearch] = useState("");

  const enrichedGroups = useMemo(() => groups.map(enrichPortfolioGroup), [groups]);

  const enrichedTrashedGroups = useMemo(
    () => trashedGroups.map(enrichPortfolioGroup),
    [trashedGroups]
  );

  const filteredGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    return enrichedGroups.filter((group) => {
      if (!q) return true;
      return (
        group.name.toLowerCase().includes(q) ||
        (group.description || "").toLowerCase().includes(q)
      );
    });
  }, [enrichedGroups, search]);

  const {
    page: groupsPage,
    totalPages: groupsTotalPages,
    paginatedItems: paginatedGroups,
    goToPage: goToGroupsPage,
    resetPage: resetGroupsPage,
    totalItems: groupsTotalItems,
    startIndex: groupsStartIndex,
    endIndex: groupsEndIndex,
  } = usePagination(filteredGroups, GROUPS_PAGE_SIZE);

  useEffect(() => {
    resetGroupsPage();
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

  const uploadsGroup = enrichedGroups.find((g) => g.id === uploadsGroupId) ?? null;

  const totals = useMemo(() => {
    let uploadCount = 0;
    let accounts = 0;
    let owed = 0;
    let processing = 0;
    let casesCreated = 0;
    groups.forEach((g) => {
      const row = enrichPortfolioGroup(g);
      const uploads = row.uploads ?? [];
      uploadCount += row.uploadCount || uploads.length;
      accounts += row.accounts || 0;
      owed += row.owed || 0;
      casesCreated += row.numberOfCases || 0;
      uploads.forEach((u) => {
        if (u.status === "processing") processing += 1;
      });
    });
    const caseRate = accounts > 0 ? (casesCreated / accounts) * 100 : 0;
    return {
      groups: groups.length,
      uploads: uploadCount,
      accounts,
      owed,
      processing,
      casesCreated,
      caseRate,
    };
  }, [groups]);

  const handleSaveGroup = async (data) => {
    setSavingGroup(true);
    try {
      if (editingGroup) {
        await updateGroup(editingGroup.id, data);
        showSuccess("Portfolio group updated.");
      } else {
        await addGroup(data);
        showSuccess("Portfolio group created.");
        goToGroupsPage(1);
      }
      setFormOpen(false);
      setEditingGroup(null);
    } catch (err) {
      showError(err.message || "Could not save portfolio group.");
    } finally {
      setSavingGroup(false);
    }
  };

  const loadTrashedGroups = async () => {
    if (!token) {
      setTrashedGroups([]);
      return;
    }
    setTrashedLoading(true);
    setTrashedError(null);
    try {
      const list = await fetchTrashedPortfolioGroups(token);
      setTrashedGroups(list.map(enrichPortfolioGroup));
    } catch (err) {
      setTrashedError(err.message || "Could not load deleted portfolio groups.");
    } finally {
      setTrashedLoading(false);
    }
  };

  useEffect(() => {
    loadTrashedGroups();
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDeleteGroup = async () => {
    if (!deletingGroup) return;
    setDeleting(true);
    try {
      await removeGroup(deletingGroup.id);
      showSuccess("Portfolio group moved to Deleted groups.");
      setDeletingGroup(null);
      if (uploadsGroupId === deletingGroup.id) setUploadsGroupId(null);
      await loadTrashedGroups();
    } catch (err) {
      showError(err.message || "Could not delete portfolio group.");
    } finally {
      setDeleting(false);
    }
  };

  const handleRestoreGroup = async (group) => {
    setRestoringId(group.id);
    try {
      await restorePortfolioGroup(token, group.id);
      showSuccess("Portfolio group restored.");
      clearPortfolioUploadsCache();
      await Promise.all([refreshGroups(), loadTrashedGroups()]);
    } catch (err) {
      showError(err.message || "Could not restore portfolio group.");
    } finally {
      setRestoringId(null);
    }
  };

  const handlePermanentDelete = async () => {
    if (!purgingGroup) return;
    setPurging(true);
    try {
      await permanentlyDeletePortfolioGroup(token, purgingGroup.id);
      showSuccess("Portfolio group permanently deleted.");
      setPurgingGroup(null);
      await loadTrashedGroups();
    } catch (err) {
      showError(err.message || "Could not permanently delete portfolio group.");
    } finally {
      setPurging(false);
    }
  };

  return (
    <div className="mx-auto max-w-8xl px-4 pb-12 pt-5 sm:px-6 lg:px-8">
      <div className="flex flex-col gap-5 border-b border-primary/[0.06] pb-6 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-widest text-accent-dark">
            Portfolio management
          </p>
          <h1 className="mt-1 text-2xl font-bold tracking-tight text-primary sm:text-[1.85rem]">
            Debt portfolio groups
          </h1>
          <p className="mt-2 max-w-xl text-sm leading-relaxed text-primary/45">
            Search and filter groups, upload Excel files, review upload history, and open imported debtor data.
          </p>
        </div>
        {canManage ? (
          <Button
            size="sm"
            onClick={() => {
              setEditingGroup(null);
              setFormOpen(true);
            }}
          >
            <Plus className="h-4 w-4" />
            New group
          </Button>
        ) : null}
      </div>

      <StatCardGrid cols={4} className="mt-4">
        <StatCard label="Portfolio groups" value={totals.groups} sub="Organise uploads by book or batch" icon={Layers} tone="primary" />
        <StatCard
          label="Excel uploads"
          value={totals.uploads}
          sub={totals.processing ? `${totals.processing} processing now` : "All uploads processed"}
          icon={FileSpreadsheet}
          tone="accent"
        />
        <StatCard label="Total accounts" value={totals.accounts.toLocaleString()} sub="Across all groups & uploads" icon={Users} tone="emerald" />
        <StatCard
          label="Total outstanding"
          value={formatCurrency(totals.owed)}
          sub={`${totals.casesCreated.toLocaleString()} recovery cases created`}
          icon={Wallet}
          tone="primary"
          progress={totals.caseRate}
        />
      </StatCardGrid>

      <div className="mt-6 overflow-hidden rounded-2xl border border-primary/[0.07] bg-white shadow-[0_2px_8px_rgba(127,29,29,0.06)]">
        <div className="flex flex-col gap-3 border-b border-primary/[0.06] px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-primary/35">Your groups</p>
            <p className="mt-0.5 text-sm text-primary/45">
              {filteredGroups.length} of {groups.length} group{groups.length !== 1 ? "s" : ""}
            </p>
          </div>
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
            <div className="relative min-w-[220px] flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-primary/30" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search groups…"
                className="w-full rounded-xl border border-primary/10 bg-white py-2.5 pl-10 pr-3 text-sm focus:border-accent/50 focus:outline-none focus:ring-2 focus:ring-accent/20"
              />
            </div>
          </div>
        </div>

        {groupsError ? (
          <p className="mb-3 px-5 pt-4 text-sm text-rose-600">{groupsError}</p>
        ) : null}
        {uploadsError ? (
          <p className="mb-3 px-5 pt-4 text-sm text-amber-700">
            Portfolio groups loaded, but file uploads could not be fetched: {uploadsError}
          </p>
        ) : null}
        {groupsLoading ? (
          <p className="px-5 py-8 text-sm text-primary/45">Loading portfolio groups…</p>
        ) : paginatedGroups.length === 0 ? (
          <div className="px-6 py-16 text-center">
            <Layers className="mx-auto h-8 w-8 text-primary/25" />
            <p className="mt-3 text-sm font-semibold text-primary">
              {groups.length === 0 ? "No groups yet" : "No groups match your filters"}
            </p>
            <p className="mt-1 text-xs text-primary/40">
              {groups.length === 0
                ? "Create a group, then upload one Excel file under it."
                : "Try a different search."}
            </p>
          </div>
        ) : (
          <div className="overflow-x-hidden">
            <table className="w-full table-fixed divide-y divide-primary/[0.06] text-left text-sm">
              <thead className="bg-primary/[0.02] text-[11px] font-bold uppercase tracking-wider text-primary/40">
                <tr>
                  <th className="px-5 py-3">Group ID</th>
                  <th className="px-5 py-3">Group</th>
                  <th className="px-5 py-3">Outstanding</th>
                  <th className="px-5 py-3">Created</th>
                  <th className="px-5 py-3 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-primary/[0.05]">
                {paginatedGroups.map((group) => {
                  const hasData = group.hasPortfolioData;
                  const actions = [
                    canUpload && !hasData
                      ? {
                          key: "upload",
                          label: "Upload file",
                          description: "Upload one Excel file to this group",
                          icon: Upload,
                          tone: "primary",
                          to: `/dashboard/portfolios/${group.id}/upload`,
                        }
                      : null,
                    hasData
                      ? {
                          key: "uploads",
                          label: "View uploaded file",
                          description: "See the Excel upload and validation details for this group",
                          icon: FileSpreadsheet,
                          tone: "sky",
                          onClick: () => setUploadsGroupId(group.id),
                        }
                      : null,
                    hasData && group.numberOfCases > 0
                      ? {
                          key: "cases",
                          label: "View group cases",
                          description: "Open all cases from this group’s portfolio",
                          icon: Briefcase,
                          tone: "emerald",
                          to: `/dashboard/portfolios/${group.id}/cases`,
                        }
                      : null,
                    canManage
                      ? {
                          key: "edit",
                          label: "Edit group name",
                          description: "Change the name or description",
                          icon: Pencil,
                          tone: "accent",
                          onClick: () => {
                            setEditingGroup(group);
                            setFormOpen(true);
                          },
                        }
                      : null,
                    canManage
                      ? {
                          key: "delete",
                          label: "Move to deleted",
                          description: "Move this group to Deleted groups if its cases are attached and have not been worked on",
                          icon: Trash2,
                          tone: "rose",
                          onClick: () => setDeletingGroup(group),
                        }
                      : null,
                  ].filter(Boolean);

                  return (
                    <tr key={group.id} className="hover:bg-primary/[0.015]">
                      <td className="px-5 py-4">
                        <span className="inline-flex items-center gap-1.5 rounded-lg bg-primary/[0.04] px-2 py-1 font-mono text-[11px] font-medium tracking-wide text-primary/60 ring-1 ring-primary/[0.06]">
                          {group.numericId ?? group.id}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex items-start gap-3">
                          <span
                            className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-primary via-primary to-[#EF4444] text-white shadow-sm"
                          >
                            <FolderOpen className="h-4 w-4" />
                          </span>
                          <div className="min-w-0">
                            <p className="font-semibold text-primary">{group.name}</p>
                            <p className="mt-0.5 line-clamp-1 text-xs text-primary/45">
                              {group.description || "No description"}
                            </p>
                            {group.hasPortfolioData ? (
                              <div className="mt-1.5 space-y-0.5 text-[11px] text-primary/50">
                                {group.latestUpload?.fileName ? (
                                  <p className="truncate font-medium text-primary/60">
                                    {group.latestUpload.fileName}
                                  </p>
                                ) : null}
                                <p>
                                  {group.numberOfCases.toLocaleString()}{" "}
                                  {group.numberOfCases === 1 ? "case" : "cases"} ·{" "}
                                  {group.portfoliosCount.toLocaleString()}{" "}
                                  {group.portfoliosCount === 1 ? "record" : "records"} ·{" "}
                                  {formatCurrency(group.owed)} outstanding
                                  {group.latestUpload?.uploadedAt
                                    ? ` · ${formatDate(group.latestUpload.uploadedAt)}`
                                    : ""}
                                </p>
                                {group.latestUpload?.validationPassRate != null ? (
                                  <p className="inline-flex items-center gap-1 text-emerald-700/80">
                                    <ShieldCheck className="h-3 w-3 shrink-0" />
                                    {formatPercent(group.latestUpload.validationPassRate)} validation
                                    pass
                                    {group.latestUpload.invalidRows
                                      ? ` · ${group.latestUpload.invalidRows} invalid row${
                                          group.latestUpload.invalidRows === 1 ? "" : "s"
                                        }`
                                      : ""}
                                  </p>
                                ) : null}
                              </div>
                            ) : (
                              <p className="mt-1.5 text-[11px] text-primary/35">No upload file yet</p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-4 tabular-nums font-semibold text-primary">
                        {formatCurrency(group.owed)}
                      </td>
                      <td className="px-5 py-4 text-primary/55">{formatDate(group.createdAt)}</td>
                      <td className="px-5 py-4 text-center">
                        <TableActionsMenu actions={actions} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <ListPagination
          page={groupsPage}
          totalPages={groupsTotalPages}
          totalItems={groupsTotalItems}
          startIndex={groupsStartIndex}
          endIndex={groupsEndIndex}
          goToPage={goToGroupsPage}
          pageSize={GROUPS_PAGE_SIZE}
          alwaysShow
        />
      </div>

      {trashedError ? <p className="mt-4 text-sm text-rose-600">{trashedError}</p> : null}
      {enrichedTrashedGroups.length > 0 ? (
        <div className="mt-6 overflow-hidden rounded-2xl border border-primary/[0.07] bg-white shadow-[0_2px_8px_rgba(127,29,29,0.06)]">
          <div className="flex flex-col gap-3 border-b border-primary/[0.06] bg-primary/[0.015] px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/[0.06] text-primary/45">
                  <Archive className="h-4 w-4" />
                </span>
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-primary/35">
                    Deleted groups
                  </p>
                  <p className="mt-0.5 text-sm text-primary/45">
                    {enrichedTrashedGroups.length} removed from your active list
                  </p>
                </div>
              </div>
              <p className="mt-3 max-w-2xl text-xs leading-relaxed text-primary/40 sm:mt-2 sm:pl-10">
                Restore puts the group back with its upload file, portfolio records, and cases.
                Permanent delete cannot be undone.
              </p>
            </div>
          </div>
          <div className="overflow-x-hidden">
            <table className="w-full table-fixed divide-y divide-primary/[0.06] text-left text-sm">
              <thead className="bg-primary/[0.02] text-[11px] font-bold uppercase tracking-wider text-primary/40">
                <tr>
                  <th className="px-5 py-3">Group ID</th>
                  <th className="px-5 py-3">Group</th>
                  <th className="px-5 py-3">Outstanding</th>
                  <th className="px-5 py-3">Deleted</th>
                  <th className="px-5 py-3 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-primary/[0.05]">
                {enrichedTrashedGroups.map((group) => {
                  const actions = [
                    {
                      key: "restore",
                      label: restoringId === group.id ? "Restoring…" : "Restore group",
                      description: "Bring this group, its file, and its portfolio records back",
                      icon: RotateCcw,
                      tone: "emerald",
                      onClick: () => {
                        if (!restoringId && !purging) handleRestoreGroup(group);
                      },
                    },
                    {
                      key: "purge",
                      label: "Delete permanently",
                      description: "Remove this group and its portfolio records and cases",
                      icon: Trash2,
                      tone: "rose",
                      onClick: () => {
                        if (!restoringId && !purging) setPurgingGroup(group);
                      },
                    },
                  ];

                  const hasRecords = group.numberOfCases > 0 || group.portfoliosCount > 0;

                  return (
                    <tr key={group.id} className="bg-primary/[0.01] hover:bg-primary/[0.025]">
                      <td className="px-5 py-4 align-top">
                        <span className="inline-flex items-center gap-1.5 rounded-lg bg-primary/[0.04] px-2 py-1 font-mono text-[11px] font-medium tracking-wide text-primary/60 ring-1 ring-primary/[0.06]">
                          {group.numericId ?? group.id}
                        </span>
                      </td>
                      <td className="px-5 py-4 align-top">
                        <div className="flex items-start gap-3">
                          <span
                            className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/[0.08] text-primary/45 ring-1 ring-primary/[0.06]"
                          >
                            <Archive className="h-4 w-4" />
                          </span>
                          <div className="min-w-0">
                            <p className="font-semibold text-primary">{group.name}</p>
                            <p className="mt-0.5 line-clamp-2 text-xs text-primary/45">
                              {group.description || "No description"}
                            </p>
                            {group.uploadCount > 0 ? (
                              <div className="mt-1.5 space-y-0.5 text-[11px] text-primary/50">
                                {group.latestUpload?.fileName ? (
                                  <p className="truncate font-medium text-primary/60">
                                    {group.latestUpload.fileName}
                                  </p>
                                ) : null}
                                <p>
                                  {group.accounts.toLocaleString()} account
                                  {group.accounts !== 1 ? "s" : ""}
                                  {group.latestUpload?.uploadedAt
                                    ? ` · ${formatDate(group.latestUpload.uploadedAt)}`
                                    : ""}
                                </p>
                              </div>
                            ) : hasRecords ? (
                              <p className="mt-1.5 text-[11px] text-primary/50">
                                {group.numberOfCases.toLocaleString()}{" "}
                                {group.numberOfCases === 1 ? "case" : "cases"} ·{" "}
                                {group.portfoliosCount.toLocaleString()}{" "}
                                {group.portfoliosCount === 1
                                  ? "portfolio record"
                                  : "portfolio records"}
                              </p>
                            ) : (
                              <p className="mt-1.5 text-[11px] text-primary/35">No records in this group</p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-4 align-top tabular-nums font-semibold text-primary/70">
                        {group.owed > 0 ? formatCurrency(group.owed) : "—"}
                      </td>
                      <td className="px-5 py-4 align-top text-primary/55">{formatDate(group.deletedAt)}</td>
                      <td className="px-5 py-4 align-top text-center">
                        <TableActionsMenu actions={actions} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      <AnimatePresence>
        {uploadsGroup ? (
          <UploadsDrawer
            key={uploadsGroup.id}
            group={uploadsGroup}
            open={Boolean(uploadsGroup)}
            onClose={() => setUploadsGroupId(null)}
            canUpload={canUpload}
          />
        ) : null}
      </AnimatePresence>

      <GroupFormPanel
        open={formOpen}
        group={editingGroup}
        saving={savingGroup}
        onClose={() => {
          if (savingGroup) return;
          setFormOpen(false);
          setEditingGroup(null);
        }}
        onSubmit={handleSaveGroup}
      />

      <DeleteGroupModal
        group={deletingGroup}
        saving={deleting}
        onClose={() => !deleting && setDeletingGroup(null)}
        onConfirm={handleDeleteGroup}
      />
      <PermanentDeleteModal
        group={purgingGroup}
        saving={purging}
        onClose={() => !purging && setPurgingGroup(null)}
        onConfirm={handlePermanentDelete}
      />
    </div>
  );
}
